import './EditPdcCard.scss'

import { InputInputPayload } from '@here/hds-input'
import { HDSButton, HDSInput, HDSTextarea, HDSToggle } from '@here/hds-react-components'
import { TextareaInputPayload } from '@here/hds-textarea'
import classNames from 'classnames'
import { HereAccount } from 'components/Account/data/reducer'
import CloseButton from 'components/App/components/CloseButton'
import { ScrollableContainer } from 'components/App/components/ScrollableContainer'
import { useFlag } from 'components/App/hooks/useFlag'
import { addNotificationToQueue } from 'components/Notifications/data/reducer'
import { useAppDispatch } from 'data/store/hooks'
import { Field, Form, FormikProps, FormikProvider, useFormik } from 'formik'
import { IzumiMessageKey } from 'izumi/IzumiMessageKey'
import { submitFeedback } from 'lib/api/feedback'
import { config } from 'lib/config'
import { i18n } from 'lib/i18n'
import React from 'react'

import { PdcCardItem } from '../PdcCard'

const ERROR_CODE_PLACE_IS_CLOSED = 32
const ERROR_CODE_PLACE_HAS_CHANGED = 31
const DEBUG_MESSAGE = 'THIS IS A TEST, PLEASE IGNORE OR DISCARD!'

interface EditPdcCardProps {
  onClose: () => void
  pdcItem: PdcCardItem
  user: HereAccount
}

interface FormValues {
  'address': string
  'mail': string
  'name': string
  'is_place_closed': boolean
  'opening-hours': string
  'phone': string
  'website': string
}

interface RequestProperties {
  'appId': string
  'details': string
  'v': string
  'error': number
  'currentValues': {
    'address': string
    'mail': string
    'name': string
    'opening-hours': string
    'phone': string
    'website': string
  }
  'address'?: string
  'mail'?: string
  'name'?: string
  'opening-hours'?: string
  'phone'?: string
  'website'?: string
}

const EditPdcCard = ({ onClose, pdcItem, user }: EditPdcCardProps): JSX.Element => {
  const showDebugMessage = useFlag('showDebugMessageOnSubmit')
  const dispatch = useAppDispatch()

  const initialValues = {
    'address': pdcItem.addressLabel ?? '',
    'is_place_closed': false,
    'mail': pdcItem.contacts?.email ?? '',
    'name': pdcItem.title,
    'opening-hours': pdcItem.openingHours?.weekSchedule.map(formatWeekSchedule).join('\n') ?? '',
    'phone': pdcItem.contacts?.phone ?? '',
    'website': pdcItem.contacts?.www ?? '',
  }

  const onSubmit = (values: FormValues): void => {
    formik.setSubmitting(true)
    const currentValues = {
      'address': initialValues.address,
      'mail': initialValues.mail,
      'name': initialValues.name,
      'opening-hours': initialValues['opening-hours'],
      'phone': initialValues.phone,
      'website': initialValues.website,
    }

    const properties: RequestProperties = {
      appId: config.APP_KEY_FEEDBACK,
      currentValues,
      details: showDebugMessage ? DEBUG_MESSAGE : '',
      error: values.is_place_closed ? ERROR_CODE_PLACE_IS_CLOSED : ERROR_CODE_PLACE_HAS_CHANGED,
      v: '2.7',
    }

    if (!values.is_place_closed) {
      if (initialValues.address !== values.address) {
        properties.address = values.address
        properties.details = `${properties.details} - WRONG ADDRESS. NEW ADDRESS = ${values.address}`
      }
      if (initialValues.mail !== values.mail) {
        properties.mail = values.mail
        properties.details = `${properties.details} - WRONG MAIL. NEW MAIL = ${values.mail}`
      }
      if (initialValues.name !== values.name) {
        properties.name = values.name
        properties.details = `${properties.details} - WRONG NAME. NEW NAME = ${values.name}`
      }
      if (initialValues['opening-hours'] !== values['opening-hours']) {
        properties['opening-hours'] = values['opening-hours']
        properties.details = `${properties.details} - WRONG OPENING_HOURS. NEW OPENING_HOURS = ${values['opening-hours']}`
      }
      if (initialValues.phone !== values.phone) {
        properties.phone = values.phone
        properties.details = `${properties.details} - WRONG PHONE. NEW PHONE = ${values.phone}`
      }
      if (initialValues.website !== values.website) {
        properties.website = values.website
        properties.details = `${properties.details} - WRONG WEBSITE. NEW WEBSITE = ${values.website}`
      }
    } else {
      properties.details = `${properties.details} - PLACE IS PERMANENTLY CLOSED`
    }

    properties.details = properties.details.trim()

    const requestBody = JSON.stringify([
      {
        coordinates: [pdcItem.position?.lng ?? 0, pdcItem.position?.lat ?? 0],
        properties,
        type: 'Point',
      },
    ])

    submitFeedback(requestBody, user.accessToken)
      .then((res) => {
        if (res.status === 200) {
          dispatch(
            addNotificationToQueue({
              content: i18n('poi-feedback.success-notification.content'),
              title: i18n('poi-feedback.success-notification.title'),
              variant: 'confirmation',
            })
          )
        }
      })
      .catch(console.error)
      .finally(() => {
        formik.setSubmitting(false)
        onClose()
      })
  }

  const formik = useFormik({ initialValues, onSubmit })

  const resetInputsValue = (_formik: FormikProps<FormValues>): void => {
    void _formik.setFieldValue('address', _formik.initialValues.address)
    void _formik.setFieldValue('mail', _formik.initialValues.mail)
    void _formik.setFieldValue('name', _formik.initialValues.name)
    void _formik.setFieldValue('opening-hours', _formik.initialValues['opening-hours'])
    void _formik.setFieldValue('phone', _formik.initialValues.phone)
    void _formik.setFieldValue('website', _formik.initialValues.website)
  }

  return (
    <FormikProvider value={formik}>
      <Form
        onSubmit={(event) => {
          event.preventDefault()
          void onSubmit(formik.values)
        }}
      >
        <article className="EditPdcCard">
          <div className="EditPdcCard__header">
            <div>
              <div className="EditPdcCard__header-title" data-testid="edit-pdc-card-title">
                {i18n('poi-feedback.title')}
              </div>
              <div className="EditPdcCard__header-subtitle" data-testid="edit-pdc-card-subtitle">
                {pdcItem.title}
              </div>
            </div>
            <CloseButton onClick={onClose} />
          </div>
          <ScrollableContainer className="EditPdcCard__field-list">
            <React.Fragment>
              <div className="EditPdcCard__close-warning">
                {i18n('poi-feedback.close-warning')}
                <HDSToggle
                  className="EditPdcCard__close-toggle"
                  data-testid="toggle-is-place-closed"
                  name=""
                  onClick={() => {
                    void formik.setFieldValue('is_place_closed', !formik.values.is_place_closed)
                    resetInputsValue(formik)
                  }}
                />
              </div>
              <CustomInputWithReset name="name" formik={formik} />
              <CustomInputWithReset name="address" formik={formik} />
              <CustomInputWithReset name="opening-hours" formik={formik} />
              <CustomInputWithReset name="phone" formik={formik} />
              <CustomInputWithReset name="mail" formik={formik} />
              <CustomInputWithReset name="website" formik={formik} />
              <footer className="EditPdcCard__footer">
                <HDSButton
                  data-testid="edit-pdc-card-submit"
                  width="100%"
                  type="submit"
                  disabled={!formik.dirty}
                  loading={formik.isSubmitting}
                >
                  {i18n('poi-feedback.send')}
                </HDSButton>
              </footer>
            </React.Fragment>
          </ScrollableContainer>
        </article>
      </Form>
    </FormikProvider>
  )
}

export const formatWeekSchedule = ({
  weekDay,
  timings,
}: {
  weekDay: string
  timings: string
}): string => `${weekDay}: ${timings}`

const CustomInputWithReset = ({
  name,
  formik,
}: {
  name: keyof FormValues
  formik: FormikProps<FormValues>
}): JSX.Element => {
  const initialFieldValue = formik.getFieldMeta(name).initialValue
  const handleResetField = (): void => void formik.setFieldValue(name, initialFieldValue)

  return (
    <div className="EditPdcCard__field-row">
      <Field
        data-testid={`edit-pdc-card-input-${name}`}
        name={name}
        onInput={(e: CustomEvent<TextareaInputPayload | InputInputPayload>) => {
          void formik.setFieldValue(name, e.detail.value)
        }}
        className={classNames({
          'EditPdcCard__field--touched': formik.getFieldMeta(name).value !== initialFieldValue,
        })}
        label={i18n(`poi-feedback.${name}` as IzumiMessageKey)}
        as={name === 'opening-hours' ? HDSTextarea : HDSInput}
        disabled={formik.values.is_place_closed}
      />
      {initialFieldValue !== formik.getFieldMeta(name).value && (
        <HDSButton
          data-testid={`reset-${name}`}
          type="button"
          variant="link"
          className="EditPdcCard__reset"
          onClick={handleResetField}
        >
          {i18n('poi-feedback.undo')}
        </HDSButton>
      )}
    </div>
  )
}

export default EditPdcCard
